import React from 'react';
import Banner from '../../../composants/Banner/Banner';
import { OperationMS, StatusId } from '../types';
import {
  isConsultationEnCours,
  isPlisOuverts,
  isAnalyseCloturee,
} from '../domain/analyse/accessAnalyse';
import { getClotureAnalyseInfo, getLancementConsultationInfo } from '../domain/analyse/historique';
import { formatDateHeure } from '../domain/analyse/dates';

// Banniere de statut contextuelle, partagee par les onglets Consultation et Analyse
// (rendue par le parent OperationContent -> aucune duplication de logique).
// Etats indexes sur l'id de statut de l'operation, contextualises par l'onglet :
//   1. Consultation en cours (2) ............. Consultation: vert (recap lancement) / Analyse: orange (invite a ouvrir les plis)
//   2. Analyse en cours apres dechiffrage (4) . orange, invite a cloturer (2 onglets)
//   3. Consultation lancee (statut > 2) ....... vert (recap lancement), onglet Consultation uniquement
//   4. Analyse cloturee (13 a signer / 11 signe) vert, recap date + operateur, onglet Analyse uniquement
interface OperationStatusBannerProps {
  operation: OperationMS;
  context: 'consultation' | 'analyse' | 'consultation-brouillon';
}

const OperationStatusBanner: React.FC<OperationStatusBannerProps> = ({ operation, context }) => {
  const statusId = operation.status?.id;
  const lancement = getLancementConsultationInfo(operation.historique);
  const dateLancement = formatDateHeure(lancement.date);
  const dateLimite = formatDateHeure(operation.date_limite_remise ?? operation.expire_at);

  // Banniere verte "La consultation a été lancée le X par Y. Dernière mise à jour de la date de remise
  // des offres : Z" — affichee des que la consultation est lancee (statut >= 2). Gardee par le contexte
  // pour ne pas la dupliquer entre les deux instances (consultation / consultation-brouillon).
  const lancementBanner =
    lancement && dateLancement && context !== 'consultation-brouillon' ? (
      <Banner
        variant="success"
        filled
        message={
          <>
            La consultation a été lancée le {dateLancement}
            {lancement.operateur ? ` par ${lancement.operateur}` : ''}. Dernière mise à jour de la date de
            remise des offres : {dateLimite ?? '—'}.
          </>
        }
      />
    ) : null;

  if (isConsultationEnCours(statusId)) {
    // Onglet Analyse : on garde l'invitation orange a deposer (l'acheteur y attend l'ouverture des
    // plis), sans le recap vert de lancement.
    if (context === 'analyse') {
      return (
        <Banner
          variant="warning"
          filled
          message={`Les candidats sont invités à déposer leurs offres jusqu'au ${
            dateLimite ?? '—'
          }. Vous pourrez ouvrir les plis et commencer l'analyse à partir de cette date.`}
        />
      );
    }
    // Onglet Consultation : recap vert du lancement, sans l'invitation orange.
    return lancementBanner;
  }

  if (statusId === StatusId.AAnalyser && isPlisOuverts(operation)) {
    return (
      <Banner
        variant="warning"
        filled
        message={`Les plis sont ouverts et déchiffrés, l'analyse des offres est en cours. Vous devez cliquer sur « Clôturer l'analyse et préparer le marché » pour passer à l'onglet suivant de préparation du marché subséquent. Aucun message n'est envoyé aux candidats à la clôture de l'analyse.`}
      />
    );
  }

  if (statusId === StatusId.Brouillon && context === 'consultation-brouillon') {
    return (
      <Banner
        variant="info"
        message="À l’instant où vous cliquerez sur « Lancer la consultation », les candidats seront informés par mail du lancement de la consultation et seront invités à répondre dans le délai 
        imparti. L’onglet suivant, intitulé « Questions/Réponses », sera également accessible afin de faciliter les échanges entre les candidats et vous."
      />
    );
  }

  if (statusId === StatusId.Brouillon) {
    return (
      <Banner
        variant="warning"
        filled
        message={
          <>
            La consultation n'a pas encore été lancée. Il convient de remplir tous les
            champs obligatoires marqués d'une<span className="required">*</span> puis
            de cliquer sur « Lancer la consultation ».
          </>
        }
      />
    );
  }

  if (statusId && statusId > 2 && lancement && dateLancement && context === 'consultation') {
    return (
        <Banner
          variant="success"
          filled
          message={
            <>
              La consultation a été lancée
              {dateLancement ? ` le ${dateLancement}` : ''}
              {lancement.operateur ? ` par ${lancement.operateur}` : ''}. Dernière mise à jour
              de la date de remise des offres : {dateLimite ?? '—'}.
            </>
          }
        />
    );
  }

  
  

  // Recap de cloture : onglet Analyse uniquement (la consultation n'a plus de message a ce stade).
  if (context === 'analyse' && isAnalyseCloturee(statusId)) {
    const cloture = getClotureAnalyseInfo(operation.historique);
    const dateCloture = formatDateHeure(cloture.date);
    const message =
      dateCloture && cloture.operateur
        ? `L'analyse des offres a été clôturée le ${dateCloture} par ${cloture.operateur}.`
        : dateCloture
        ? `L'analyse des offres a été clôturée le ${dateCloture}.`
        : `L'analyse des offres a été clôturée.`;
    return <Banner variant="success" filled message={message} />;
  }

  return null;
};

export default OperationStatusBanner;
